"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useId, useState } from "react"
import { Link } from "react-router-dom"
import { Info } from "lucide-react"
import { GLOSSARY_LINKS, type GlossaryTermId } from "@bitebalance/i18n"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useI18n } from "@/i18n"
import { glossaryTerm, glossaryUi } from "@/lib/glossary"

/**
 * The ⓘ info control (Budget Model v2 spec §14). A REAL button — keyboard
 * and screen-reader reachable — that opens a small dialog with the term's
 * canonical name, its short definition and a "Learn more" link into the
 * Guide Center Glossary. Click / Enter / Space toggle; Escape closes and
 * returns focus to the button; an outside click closes (all provided by the
 * Radix popover primitive); a visible Close button (owner feedback
 * 2026-09-05) gives touch users an obvious way out. Hover is deliberately
 * NOT a path here: the dialog is the one path, so it works identically with
 * a mouse, a keyboard, a touch screen and assistive tech.
 *
 * Placement: the dialog opens below the button. Put the button AFTER the
 * amount it explains (or beside a heading), never above it, so the
 * definition never covers the number the user is asking about.
 *
 * Props reference a STABLE glossary id, never a string: nobody outside the
 * glossary module defines what a budget term means.
 */
export function InfoButton({ termId, className }: { termId: GlossaryTermId; className?: string }) {
  const { t, locale } = useI18n()
  const [open, setOpen] = useState(false)
  const contentId = useId()
  const titleId = useId()
  const term = glossaryTerm(termId, locale)
  const ui = glossaryUi(locale)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("views.budget.infoAria", { name: term.name })}
          aria-controls={contentId}
          data-glossary-term={termId}
          className={
            "inline-flex size-6 shrink-0 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-cream hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-green " +
            (className ?? "")
          }
        >
          <Info aria-hidden="true" className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        id={contentId}
        role="dialog"
        aria-labelledby={titleId}
        align="start"
        sideOffset={8}
        collisionPadding={8}
        className="w-80 rounded-bb-xl border-line bg-paper p-4 shadow-[var(--bb-shadow)]"
      >
        <p id={titleId} className="text-sm font-semibold text-ink">
          {term.name}
        </p>
        <p className="mt-1 text-sm text-ink-2">{term.shortDefinition}</p>
        <div className="mt-3 flex items-center justify-between gap-3">
          <Link
            to={GLOSSARY_LINKS.web(termId)}
            className="text-sm font-semibold text-green underline"
            onClick={() => setOpen(false)}
          >
            {ui.learnMore}
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="min-h-9 rounded-bb-md px-2 text-sm font-semibold text-ink-2 hover:bg-cream hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-green"
          >
            {ui.close}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
